import { useAuth } from '@hb/react'
import { USER_ROLES, UserRoleItem } from '@hb/shared'
import { collection, doc, DocumentReference, getDoc } from 'firebase/firestore'
import { deleteToken, getToken, Messaging } from 'firebase/messaging'
import { UAParser } from 'ua-parser-js'
import { db } from '../backend/db/db'
import {
  registerWebPushNotificationToken,
  unregisterWebPushNotificationToken,
} from '../backend/functions/other'
// import {
//   db,
//   registerWebPushNotificationToken,
//   unregisterWebPushNotificationToken,
// } from '../backend'

const TOKEN_EXPIRATION = 1000 * 60 * 60 * 24 * 30 // 30 days

export const getPushNotificationToken = async (
  messaging: Messaging,
  vapidKey: string,
  hbDeviceId: string,
): Promise<string | null> => {
  const { authUser } = useAuth.getState()
  const uid = authUser?.uid
  if (!uid) {
    throw new Error('User not authenticated')
  }

  const roleItemDoc = await getDoc(
    doc(collection(db, USER_ROLES), uid) as DocumentReference<UserRoleItem>,
  )
  const { webPushNotificationsToken } = roleItemDoc.data() ?? {}
  if (
    webPushNotificationsToken &&
    webPushNotificationsToken.timestamp + TOKEN_EXPIRATION < Date.now()
  ) {
    return createNewPushNotificationToken(messaging, vapidKey, hbDeviceId)
  }
  return webPushNotificationsToken?.token ?? null
}

export const createNewPushNotificationToken = async (
  messaging: Messaging,
  vapidKey: string,
  hbDeviceId: string,
) => {
  const parser = new UAParser()
  const result = parser.getResult()
  const newToken = await getToken(messaging, { vapidKey })
  await registerWebPushNotificationToken({
    token: newToken,
    device: {
      browser: result.browser.name ?? '',
      model: result.device.model ?? '',
      type: result.device.type ?? '',
      hbDeviceId,
      vendor: result.device.vendor ?? '',
      os: result.os.name ?? '',
    },
  })
  return newToken
}

export const requestPushNotificationPermission = async () => Notification.requestPermission()

export const assertPushNotificationToken = async (
  messaging: Messaging,
  vapidKey: string,
  hbDeviceId: string,
) => {
  const notificationToken = await getPushNotificationToken(messaging, vapidKey, hbDeviceId)
  if (!notificationToken) {
    const permission = await requestPushNotificationPermission()
    if (permission === 'granted') {
      await createNewPushNotificationToken(messaging, vapidKey, hbDeviceId)
      return true
    }
    return false
  }
  return true
}

export const updatePushNotificationDevice = async (
  messaging: Messaging,
  vapidKey: string,
  hbDeviceId: string,
) => {
  try {
    await deleteToken(messaging)
  } catch (e) {
    console.error('Failed to delete token:', e)
  }
  return createNewPushNotificationToken(messaging, vapidKey, hbDeviceId)
}

export const enableWebPushNotifications = async (
  messaging: Messaging,
  vapidKey: string,
  hbDeviceId: string,
) => {
  await createNewPushNotificationToken(messaging, vapidKey, hbDeviceId)
}

export const unregisterPushNotifications = async (messaging: Messaging) => {
  const { authUser } = useAuth.getState()
  const uid = authUser?.uid
  if (!uid) {
    throw new Error('User not authenticated')
  }
  try {
    await deleteToken(messaging)
  } catch (err: any) {
    console.error(err)
  }
  await unregisterWebPushNotificationToken()
}
